import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Display an employee's passport photo. Fetches as blob (auth cookie needed).
 * Falls back to initials if no photo or fetch fails.
 * Uses `passport_photo_path` presence as a signal; also refetches when
 * `passport_photo_updated_at` changes (for cache-busting after upload).
 */
export default function PassportPhoto({ employee, className = "", initialsClassName = "" }) {
    const [url, setUrl] = useState(null);
    const [failed, setFailed] = useState(false);
    const empId = employee?.id;
    const updated = employee?.passport_photo_updated_at;
    const hasStoredPhoto = !!employee?.passport_photo_path;
    const legacyPhoto = employee?.photo;

    useEffect(() => {
        let alive = true;
        let blobUrl = null;
        setFailed(false);
        if (empId && hasStoredPhoto) {
            (async () => {
                try {
                    const res = await api.get(`/employees/${empId}/passport-photo`, { responseType: "blob" });
                    if (!alive) return;
                    blobUrl = URL.createObjectURL(res.data);
                    setUrl(blobUrl);
                } catch (e) {
                    if (alive) setFailed(true);
                }
            })();
        } else {
            setUrl(null);
        }
        return () => {
            alive = false;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [empId, updated, hasStoredPhoto]);

    const finalSrc = url || (legacyPhoto && !failed ? legacyPhoto : null);

    if (finalSrc) {
        return <img src={finalSrc} alt="" className={className} data-testid="passport-photo-img" />;
    }
    const initials = ((employee?.first_name?.[0] || "") + (employee?.last_name?.[0] || "")).toUpperCase() || "?";
    return (
        <div className={`flex items-center justify-center bg-ink/5 text-ink/30 font-display font-black ${initialsClassName}`} data-testid="passport-photo-initials">
            {initials}
        </div>
    );
}
